<script setup lang="ts">
// The coin is supplied artwork, not something to redraw: ElfieCare's own, lavender
// (Dao, 1 Oct 2026), taken verbatim from ElfieCare's logo file.
import coin from '@/assets/logo/elfie-coin.svg'

interface Props {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Inverted sits on the navy field of the app chrome. */
  inverted?: boolean
}
withDefaults(defineProps<Props>(), { size: 'md', inverted: false })
</script>

<template>
  <!--
    The product lockup: ElfieCare's own lavender coin plus "Elfie" in semibold and
    the product name in italic, per the design system's ElfieWordmark.
  -->
  <span :class="['lockup', `lockup--${size}`, { 'lockup--inverted': inverted }]">
    <img :src="coin" class="lockup__coin" alt="" aria-hidden="true" />
    <span class="lockup__word">
      <span class="lockup__brand">Elfie</span><span class="lockup__product">Care Intelligence</span>
    </span>
  </span>
</template>

<style scoped>
.lockup {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-md);
  font-family: var(--font-family-primary);
  color: var(--fg-primary);
}

/*
 * On the navy field, and it stays light in BOTH themes — the field is dark in
 * each. `--color-base-white` looks right in light mode but the dark theme
 * remaps it to a near-black surface value, which is why the wordmark went dark
 * on the navy. `--fg-on-field` is the token that means "text on the field".
 */
.lockup--inverted {
  color: var(--fg-on-field);
}

.lockup__coin {
  display: block;
  flex-shrink: 0;
}

.lockup--sm .lockup__coin {
  width: 1.25rem;
  height: 1.25rem;
}
.lockup--md .lockup__coin {
  width: 2rem;
  height: 2rem;
}
.lockup--lg .lockup__coin {
  width: 2.25rem;
  height: 2.25rem;
}
/* The auth screen only. The wordmark is the whole left column there, so it is
   set at display scale rather than heading scale. */
.lockup--xl .lockup__coin {
  width: 3rem;
  height: 3rem;
}

.lockup__word {
  letter-spacing: -0.01em;
}

.lockup--sm .lockup__word {
  font-size: var(--font-size-body-3);
}
.lockup--md .lockup__word {
  font-size: var(--font-size-h5);
}
.lockup--lg .lockup__word {
  font-size: var(--font-size-h4);
}
.lockup--xl .lockup__word {
  font-size: var(--font-size-display-3);
}

.lockup__brand {
  font-weight: var(--font-weight-semibold);
}

/* The product name is set in true Inter Italic, per the brand lockup. */
.lockup__product {
  font-weight: var(--font-weight-regular);
  font-style: italic;
}
</style>
